import { type FC, lazy, memo, Suspense, useEffect } from 'react';

import styles from './DeliveryMap.module.css';
import { loadDeliveryMapWhenVisible } from './preload';

// CSS нужен уже в пререндере главной; код карты по-прежнему загружается лениво.

// Одинаковое дерево компонентов на сервере и в браузере; DOM-обёртки нет.
interface MapProps {
  className: string;
}

const withHydration = (Component: FC<MapProps>, onHydrated?: () => void): FC<MapProps> => {
  const HydratedMap: FC<MapProps> = (props) => {
    useEffect(() => onHydrated?.(), []);
    return <Component {...props} />;
  };
  return HydratedMap;
};

// Карта целиком есть в пререндере — высота секции известна сразу, страница не прыгает.
// Код карты (~50 КБ gzip) в браузере грузится отдельно и не держит первый экран: пока его нет,
// React оставляет на месте серверный HTML карты (Suspense при гидратации). Даже если фоновая
// докачка уже получила чанк, lazy отдаёт его только при подходе к секции или взаимодействии.
// Генератору HTML нужен готовый компонент — как у страниц в app/router/routes.tsx
const DeliveryMap = import.meta.env.SSR
  ? withHydration((await import('./DeliveryMap')).DeliveryMap)
  : lazy(() => {
      const { module, onHydrated } = loadDeliveryMapWhenVisible();
      return module.then((m) => ({ default: withHydration(m.DeliveryMap, onHydrated) }));
    });

// Использование класса здесь включает CSS в пререндер главной, сохраняя код карты ленивым.
const Section: FC = () => (
  <Suspense fallback={null}>
    <DeliveryMap className={styles.root} />
  </Suspense>
);

// observer главной в StrictMode (yarn dev) переподписывается и повторяет рендер до того, как
// пришёл чанк карты: новые props Suspense прерывали гидратацию и включали пустой fallback —
// секция пропадала на ~0,1 с, всё ниже съезжало (2778 → 2079 → 2778, видео владельца 29.10.2026).
// memo держит границу при рендере родителя; разрешение lazy само довершает гидратацию
export const DeliveryMapSection = memo(Section);
